<script setup>
import { ref } from 'vue';
import ModalOverlay from './ModalOverlay.vue';

const emit = defineEmits(['save-recurrence']);

const modalRef = ref(null);

const repeatEvery = ref(1);
const repeatUnit = ref('semana');
const repeatUnits = ref([
  { label: 'dia', value: 'dia' },
  { label: 'semana', value: 'semana' },
  { label: 'mês', value: 'mes' },
  { label: 'ano', value: 'ano' }
]);

const daysOfWeek = ref([
  { id: 1, label: 'S', name: 'Segunda', selected: false },
  { id: 2, label: 'T', name: 'Terça', selected: false },
  { id: 3, label: 'Q', name: 'Quarta', selected: true },
  { id: 4, label: 'Q', name: 'Quinta', selected: false },
  { id: 5, label: 'S', name: 'Sexta', selected: false },
  { id: 6, label: 'S', name: 'Sábado', selected: false },
  { id: 7, label: 'D', name: 'Domingo', selected: false }
]);

const endType = ref('never');
const endDate = ref('');
const endOccurrences = ref(1);

const toggleDay = (day) => {
  day.selected = !day.selected;
};

const openModal = (initialData = null) => {
  if (initialData) {
    repeatEvery.value = initialData.repeatEvery || 1;
    repeatUnit.value = initialData.repeatUnit || 'semana';
    if (initialData.days) {
      daysOfWeek.value.forEach(d => {
        d.selected = initialData.days.includes(d.id);
      });
    }
    endType.value = initialData.endType || 'never';
    endDate.value = initialData.endDate || '';
    endOccurrences.value = initialData.endOccurrences || 1;
  }
  if (modalRef.value) {
    modalRef.value.openModal();
  }
};

const closeModal = () => {
  if (modalRef.value) {
    modalRef.value.closeModal();
  }
};

const confirm = () => {
  const selectedDays = daysOfWeek.value.filter(d => d.selected).map(d => d.id);
  emit('save-recurrence', {
    repeatEvery: repeatEvery.value,
    repeatUnit: repeatUnit.value,
    days: selectedDays,
    endType: endType.value,
    endDate: endDate.value,
    endOccurrences: endOccurrences.value
  });
  closeModal();
};

defineExpose({
  openModal,
  closeModal
});
</script>

<template>
  <ModalOverlay ref="modalRef">
    <template #header>
      <div class="d-flex justify-content-between align-items-center">
        <h5 class="m-0 font-docs font-weight-bold" style="font-size: 18px; color: #212529;">
          Recorrência personalizada
        </h5>
        <button type="button" class="close" @click="closeModal">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
    </template>

    <template #body>
      <div class="recurrence-body font-docs">
        <div class="d-flex align-items-center mb-4">
          <span class="mr-3 text-secondary" style="font-size: 14px; min-width: 100px;">Repetir a cada:</span>
          <input
            type="number"
            min="1"
            v-model.number="repeatEvery"
            class="form-control mr-2 text-center"
            style="width: 70px; height: 38px; border-radius: 6px; border: 1px solid #ced4da;"
          />
          <select
            v-model="repeatUnit"
            class="form-select"
            style="width: 140px; height: 38px; border-radius: 6px; border: 1px solid #ced4da;"
          >
            <option v-for="unit in repeatUnits" :key="unit.value" :value="unit.value">
              {{ unit.label }}
            </option>
          </select>
        </div>

        <div v-if="repeatUnit === 'semana'" class="mb-4">
          <div class="text-secondary mb-2" style="font-size: 14px;">Repetir</div>
          <div class="d-flex gap-2">
            <button
              v-for="d in daysOfWeek"
              :key="d.id"
              type="button"
              :class="['day-chip', d.selected ? 'day-chip--active' : '']"
              @click="toggleDay(d)"
              :title="d.name"
            >
              {{ d.label }}
            </button>
          </div>
        </div>

        <div class="mb-2">
          <div class="text-secondary mb-2" style="font-size: 14px;">Termina em</div>
          
          <div class="form-check d-flex align-items-center mb-2">
            <input
              class="form-check-input recurrence-radio"
              type="radio"
              name="recurrenceEnd"
              id="endNever"
              value="never"
              v-model="endType"
            />
            <label class="form-check-label ml-2" for="endNever" style="cursor: pointer; font-size: 14px;">
              Nunca
            </label>
          </div>

          <div class="form-check d-flex align-items-center mb-2">
            <input
              class="form-check-input recurrence-radio"
              type="radio"
              name="recurrenceEnd"
              id="endDateOpt"
              value="on_date"
              v-model="endType"
            />
            <label class="form-check-label ml-2 mr-2" for="endDateOpt" style="cursor: pointer; font-size: 14px; min-width: 25px;">
              Em
            </label>
            <input
              type="date"
              v-model="endDate"
              :disabled="endType !== 'on_date'"
              class="form-control"
              style="width: 160px; height: 36px; border-radius: 6px; border: 1px solid #ced4da;"
            />
          </div>

          <div class="form-check d-flex align-items-center">
            <input
              class="form-check-input recurrence-radio"
              type="radio"
              name="recurrenceEnd"
              id="endOccurrencesOpt"
              value="after_occurrences"
              v-model="endType"
            />
            <label class="form-check-label ml-2 mr-2" for="endOccurrencesOpt" style="cursor: pointer; font-size: 14px; min-width: 35px;">
              Após
            </label>
            <input
              type="number"
              min="1"
              v-model.number="endOccurrences"
              :disabled="endType !== 'after_occurrences'"
              class="form-control mr-2 text-center"
              style="width: 70px; height: 36px; border-radius: 6px; border: 1px solid #ced4da;"
            />
            <span style="font-size: 14px; color: #495057;">ocorrências</span>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="d-flex justify-content-end gap-2">
        <button
          type="button"
          class="btn btn-outline-custom font-docs"
          @click="closeModal"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="btn btn-solid-custom font-docs"
          @click="confirm"
        >
          Concluir
        </button>
      </div>
    </template>
  </ModalOverlay>
</template>

<style scoped>
.font-docs {
  font-family: inherit;
}

.day-chip {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid #dee2e6;
  background-color: #f8f9fa;
  color: #6c757d;
  font-weight: 600;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
}

.day-chip:hover {
  border-color: #F1860B;
  color: #F1860B;
}

.day-chip--active {
  background-color: #F1860B !important;
  border-color: #F1860B !important;
  color: #ffffff !important;
}

.recurrence-radio:checked {
  background-color: #F1860B;
  border-color: #F1860B;
}

.btn-outline-custom {
  color: #F1860B;
  border: 1px solid #F1860B;
  background: transparent;
  font-weight: 600;
  padding: 6px 18px;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.btn-outline-custom:hover {
  background-color: rgba(241, 134, 11, 0.08);
}

.btn-solid-custom {
  background-color: #F1860B;
  color: #ffffff;
  border: 1px solid #F1860B;
  font-weight: 600;
  padding: 6px 20px;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.btn-solid-custom:hover {
  background-color: #d97405;
  border-color: #d97405;
}
</style>
